import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AyudaButton from './AyudaButton.jsx';

/**
 * Encabezado estándar de página. Mantiene tipografía y espaciado
 * consistente en todos los componentes.
 *
 * Props:
 *   icon       — componente de icono (de @mui/icons-material)
 *   titulo     — texto principal
 *   subtitulo  — texto chico debajo (opcional, ej: stats)
 *   ayuda      — string (tooltip simple) | { titulo, contenido } (modal de ayuda)
 *   accion     — node renderizado en el lado derecho (ej: botón "Nuevo")
 *
 * Ejemplos:
 *   <TituloPagina icon={Person} titulo="Usuarios" />
 *   <TituloPagina icon={LocalOffer} titulo="Cupones" ayuda="Descuentos aplicables al checkout" />
 *   <TituloPagina icon={Receipt} titulo="Ventas"
 *      ayuda={{ titulo: 'Ayuda', contenido: <Box>...</Box> }}
 *      accion={<Button variant="contained">Nueva venta</Button>} />
 */
function TituloPagina({ icon: Icon, titulo, subtitulo, ayuda, accion }) {
  let helpEl = null;
  if (typeof ayuda === 'string' && ayuda.trim()) {
    helpEl = (
      <Tooltip title={ayuda} arrow placement="right">
        <IconButton size="small" sx={{ ml: 0.5, color: 'text.secondary' }}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  } else if (ayuda && typeof ayuda === 'object') {
    helpEl = <AyudaButton titulo={ayuda.titulo} contenido={ayuda.contenido} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        mt: 1,
        mb: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        {Icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            noWrap
          >
            {titulo}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        {helpEl}
      </Box>
      {accion && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{accion}</Box>}
    </Box>
  );
}

export default TituloPagina;
